/**
 * WaveformCanvas Component
 * Main interactive canvas for waveform visualization
 * Supports zoom, scroll, selection, and seek
 */
import { useRef, useEffect, useCallback, useState } from 'react';
import { motion } from 'framer-motion';

export const WaveformCanvas = ({
  audioBuffer,
  waveformData = [],
  duration = 0,
  currentTime = 0,
  zoom = 1,
  onSeek,
  onSelectionChange,
  selection = null,
  fadeIn = 0,
  fadeOut = 0,
  loopStart = null,
  loopEnd = null,
  isPlaying = false,
  className = '',
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [localSelection, setLocalSelection] = useState(selection);

  // Generate waveform data from AudioBuffer if not provided
  const generateWaveformData = useCallback((buffer, samples = 1000) => {
    if (!buffer) return [];
    
    const channelData = buffer.getChannelData(0);
    const blockSize = Math.floor(channelData.length / samples);
    const peaks = [];
    
    for (let i = 0; i < samples; i++) {
      const start = blockSize * i;
      let max = 0;
      
      for (let j = 0; j < blockSize; j++) {
        const value = Math.abs(channelData[start + j] || 0);
        if (value > max) max = value;
      }
      
      peaks.push(max);
    }
    
    return peaks;
  }, []);

  // Draw waveform on canvas
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    
    // Clear canvas
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, width, height);
    
    // Get or generate waveform data
    const data = waveformData.length > 0 
      ? waveformData 
      : generateWaveformData(audioBuffer, width);
    
    if (data.length === 0) {
      // Draw empty state
      ctx.fillStyle = '#27272a';
      ctx.fillRect(0, height / 2 - 1, width, 2);
      return;
    }
    
    const barWidth = width / data.length;
    const centerY = height / 2;
    
    // Draw selection background
    if (localSelection) {
      const startX = (localSelection.start / duration) * width;
      const endX = (localSelection.end / duration) * width;
      
      ctx.fillStyle = 'rgba(99, 102, 241, 0.2)';
      ctx.fillRect(startX, 0, endX - startX, height);
    }
    
    // Draw loop region
    if (loopStart !== null && loopEnd !== null) {
      const loopStartX = (loopStart / duration) * width;
      const loopEndX = (loopEnd / duration) * width;
      
      ctx.fillStyle = 'rgba(251, 191, 36, 0.1)';
      ctx.fillRect(loopStartX, 0, loopEndX - loopStartX, height);
    }
    
    // Draw waveform bars with gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, '#818cf8');
    gradient.addColorStop(0.5, '#4f46e5');
    gradient.addColorStop(1, '#818cf8');
    
    ctx.fillStyle = gradient;
    
    data.forEach((peak, i) => {
      const x = i * barWidth;
      const barHeight = peak * height * 0.8;
      
      // Apply fade in/out effect to waveform
      let opacity = 1;
      const timeAtBar = (i / data.length) * duration;
      
      if (fadeIn > 0 && timeAtBar < fadeIn) {
        opacity = timeAtBar / fadeIn;
      }
      if (fadeOut > 0 && timeAtBar > duration - fadeOut) {
        opacity = (duration - timeAtBar) / fadeOut;
      }
      
      ctx.globalAlpha = opacity;
      ctx.fillRect(x, centerY - barHeight / 2, Math.max(1, barWidth - 1), barHeight);
    });
    
    ctx.globalAlpha = 1;
    
    // Draw center line
    ctx.fillStyle = '#27272a';
    ctx.fillRect(0, centerY - 0.5, width, 1);
    
    // Draw playhead
    if (duration > 0) {
      const playheadX = (currentTime / duration) * width;
      
      ctx.fillStyle = '#22d3ee';
      ctx.shadowColor = 'rgba(34, 211, 238, 0.5)';
      ctx.shadowBlur = 10;
      ctx.fillRect(playheadX - 1, 0, 2, height);
      ctx.shadowBlur = 0;
      
      // Playhead handle
      ctx.beginPath();
      ctx.arc(playheadX, 8, 6, 0, Math.PI * 2);
      ctx.fill();
    }
    
  }, [waveformData, audioBuffer, duration, currentTime, localSelection, fadeIn, fadeOut, loopStart, loopEnd, generateWaveformData]);

  // Resize canvas to container
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        canvas.width = width * window.devicePixelRatio;
        canvas.height = height * window.devicePixelRatio;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        
        const ctx = canvas.getContext('2d');
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        
        drawWaveform();
      }
    });
    
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [drawWaveform]);

  // Redraw on data changes
  useEffect(() => {
    drawWaveform();
  }, [drawWaveform]);

  // Handle mouse events for selection and seek
  const handleMouseDown = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const time = (x / rect.width) * duration;
    
    if (e.shiftKey) {
      // Start selection
      setIsDragging(true);
      setDragStart(time);
      setLocalSelection({ start: time, end: time });
    } else {
      // Seek
      onSeek?.(time);
    }
  }, [duration, onSeek]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || dragStart === null) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const time = Math.max(0, Math.min((x / rect.width) * duration, duration));
    
    setLocalSelection({
      start: Math.min(dragStart, time),
      end: Math.max(dragStart, time),
    });
  }, [isDragging, dragStart, duration]);

  const handleMouseUp = useCallback(() => {
    if (isDragging && localSelection) {
      onSelectionChange?.(localSelection);
    }
    setIsDragging(false);
    setDragStart(null);
  }, [isDragging, localSelection, onSelectionChange]);

  // Touch support
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleMouseDown({ clientX: touch.clientX, clientY: touch.clientY, shiftKey: false });
  }, [handleMouseDown]);

  return (
    <motion.div
      ref={containerRef}
      className={`relative w-full h-full min-h-[80px] bg-zinc-950/50 border-y border-zinc-800 overflow-hidden ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-testid="waveform-canvas"
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 cursor-pointer"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
      />
      
      {/* Time ruler */}
      <div className="absolute top-0 left-0 right-0 h-5 bg-zinc-900/80 border-b border-zinc-800 flex items-center text-[10px] font-mono text-zinc-500">
        {duration > 0 && Array.from({ length: Math.ceil(duration / (zoom > 2 ? 1 : 5)) + 1 }).map((_, i) => {
          const time = i * (zoom > 2 ? 1 : 5);
          if (time > duration) return null;
          const x = (time / duration) * 100;
          return (
            <span 
              key={i} 
              className="absolute transform -translate-x-1/2"
              style={{ left: `${x}%` }}
            >
              {Math.floor(time / 60)}:{String(Math.floor(time % 60)).padStart(2, '0')}
            </span>
          );
        })}
      </div>
      
      {/* No audio state */}
      {!audioBuffer && waveformData.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-zinc-600 text-sm">
          No audio loaded
        </div>
      )}
    </motion.div>
  );
};

export default WaveformCanvas;
